import React, { MouseEvent, memo, useEffect, useState } from 'react';
import { Modal } from '@/components/_common';
import PolarisIcon from '@/components/_common/PolarisIcon';
import {
  CustomerProfile,
  Exception,
  IngestFile,
  IngestFileHistory,
  IngestManagementFileHistoryResponse,
  OrderJobAsset,
  OrderJobTask,
  Pagination
} from '@/redux/slices/slice_types';
import styled from '@emotion/styled';
import { Timeline } from '@mui/lab';
import { Button, IconButton } from '@mui/material';
import { AxiosResponse } from 'axios';
import axios from '../../../_utils/axios';
import { selectCurrentTableKey } from '@/redux/selectors/dataTableSelectors';
import OverflowMenuItem from '../OverflowMenu/OverflowMenuItem';
import HistoryTimelineItem from './HistoryTimelineItem';
import { DataTableConfig } from '@/__configs/config_types';
import { selectCanViewProfileHistory } from '@/redux/selectors/authSelectors';
import { useAppSelector } from '@/redux/hooks';
import { useConfigValue } from '@/components/_hooks/useConfigValue';

const defaultDisplayedHistoryKeys = ['status', 'assigned_user', 'id', 'hid', 'priority'];

export type EnabledHistoryTable = {
  url: string;
  type: string;
  permission?: any;
  displayedHistoryKeys?: string[];
};

interface Props {
  data: IngestFile | OrderJobTask | OrderJobAsset | CustomerProfile | Exception;
  iconOnly?: boolean;
  className?: string;
}

const History = (props: Props) => {
  const { data, iconOnly = false, className } = props;
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<IngestFileHistory[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [page, setPage] = useState(0);
  const currentTable = useAppSelector(selectCurrentTableKey);
  const ingestManagementVersion = useConfigValue(['ingestManagementConfig', 'version']) || 'v1';

  const enabledHistoryTables: Partial<Record<keyof DataTableConfig, EnabledHistoryTable>> = {
    pendingAssets: {
      url: 'core/jobs/assets',
      type: 'asset'
    },
    tasks: {
      url: 'core/tasks',
      type: 'task'
    },
    fileIngest: {
      url: ingestManagementVersion === 'v1' ? 'inventory/file_ingests' : 'ingest_manager/ingest_records',
      type: 'file'
    },
    exceptions: {
      url: 'monitoring/events',
      type: 'exception'
    },
    profiles: {
      url: 'core/profile_templates',
      type: 'profile',
      permission: selectCanViewProfileHistory,
      displayedHistoryKeys: [
        'customer_name',
        'name',
        'workflow',
        'workflow_type',
        'media_resolution',
        'audio_tracks',
        'subtitle_tracks',
        'template_inputs',
        'category_1',
        'category_2'
      ]
    }
  };

  const hasPermissions: boolean = enabledHistoryTables?.[currentTable as keyof typeof enabledHistoryTables]?.permission
    ? useAppSelector(enabledHistoryTables?.[currentTable as keyof typeof enabledHistoryTables]?.permission)
    : true;

  useEffect(() => {
    if (open) {
      setLoading(true);
      const url = enabledHistoryTables?.[currentTable as keyof typeof enabledHistoryTables]?.url || enabledHistoryTables?.tasks?.url;
      const displayedHistoryKeys =
        enabledHistoryTables?.[currentTable as keyof typeof enabledHistoryTables]?.displayedHistoryKeys || defaultDisplayedHistoryKeys;
      axios
        .post(`/${url}/${data.id}/history/?page_size=6&page_num=${page}`, {
          changed_fields_filter: displayedHistoryKeys
        })
        .then((res: AxiosResponse<IngestManagementFileHistoryResponse>) => {
          const newHistoryData = res?.data?.data || [];
          setPagination(res.data?.pagination);
          setHistory([...history, ...newHistoryData]);
          setLoading(false);
        })
        .catch(() => {
          setHistory([]);
          setLoading(false);
        });
    } else {
      setHistory([]);
      setPage(0);
      setPagination(null);
    }
  }, [open, page]);

  const loadOlderHistory = () => {
    setPage(page + 1);
  };

  const handleModalOpen: React.MouseEventHandler = (e) => {
    e.stopPropagation();
    setOpen(true);
  };

  const handleModalClose = (e: MouseEvent) => {
    e.stopPropagation();
    setOpen(false);
  };

  if (!hasPermissions) {
    return null;
  }

  const filteredHistory = history?.filter((item) => item.changes?.status?.old !== null && item.changes?.status?.old !== '');
  return (
    <div className={className}>
      {iconOnly ? (
        <IconButton title="View History" color="primary" data-testid="modal-open-icon" sx={{ m: -1 }} onClick={handleModalOpen}>
          <PolarisIcon icon="history" height={26} />
        </IconButton>
      ) : (
        <OverflowMenuItem data-testid="modal-open" key="history" onClick={handleModalOpen}>
          <PolarisIcon icon="history" style={{ height: 18 }} /> View History
        </OverflowMenuItem>
      )}

      <Modal
        loading={loading}
        open={open}
        title={
          <>
            View History: <strong>{(data as IngestFile | OrderJobTask | OrderJobAsset)?.hid || (data as CustomerProfile)?.name}</strong>
          </>
        }
        onClose={handleModalClose}
        icon={<PolarisIcon icon="history" />}
      >
        <StyledTimeline>
          {!filteredHistory.length
            ? `No ${enabledHistoryTables?.[currentTable as keyof typeof enabledHistoryTables]?.type} history found.`
            : filteredHistory.map((item, i) => (
                <HistoryTimelineItem
                  item={item}
                  type={enabledHistoryTables?.[currentTable as keyof typeof enabledHistoryTables]?.type as string}
                  key={`${item.history_date}_${i}`}
                  index={i}
                />
              ))}
        </StyledTimeline>
        {pagination && pagination.total > history.length && (
          <ViewOlder onClick={loadOlderHistory}>
            <Button size="small">View older</Button>
          </ViewOlder>
        )}
      </Modal>
    </div>
  );
};

export default memo(History);

const StyledTimeline = styled(Timeline)`
  padding-left: 10px;

  & .MuiTimelineItem-root::before {
    display: none;
  }
`;

const ViewOlder = styled.div`
  margin-top: ${({ theme }) => theme.spacing(1)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  margin-left: ${({ theme }) => theme.spacing(5)};
  display: flex;
  overflow-x: hidden;

  & .MuiButton-root {
    font-size: 0.85rem;
  }
`;
